<template>
  <div class="independent-wrapper">
    <div class="content">
      <div class="title">
        <span class="border"></span>
        <i class="el-icon-location-outline"></i> 商城管理 > 平台账户
      </div>

      <!-- 账户汇总卡片 -->
      <div class="account-cards" v-loading="accountLoading">
        <div class="acc-card">
          <div class="acc-label">可用余额</div>
          <div class="acc-value">¥{{ formatPrice(account.balance) }}</div>
        </div>
        <div class="acc-card">
          <div class="acc-label">累计收入</div>
          <div class="acc-value">¥{{ formatPrice(account.totalIncome) }}</div>
        </div>
        <div class="acc-card">
          <div class="acc-label">佣金累计</div>
          <div class="acc-value">¥{{ formatPrice(account.commissionTotal) }}</div>
        </div>
        <div class="acc-card">
          <div class="acc-label">冻结余额</div>
          <div class="acc-value">¥{{ formatPrice(account.frozenBalance) }}</div>
        </div>
      </div>

      <!-- 切换 Tab（自定义静态分段，避免 el-tabs 的 position:relative 穿透左侧飞出菜单） -->
      <div class="seg-tabs">
        <div :class="['seg-item', { 'is-active': activeTab === 'commissions' }]" @click="activeTab = 'commissions'">佣金记录</div>
        <div :class="['seg-item', { 'is-active': activeTab === 'transactions' }]" @click="activeTab = 'transactions'">账户流水</div>
      </div>

      <div class="seg-panes">
        <div v-show="activeTab === 'commissions'">
          <el-table
            :header-cell-style="{ background: '#f7f7f7', color: '#333' }"
            style="width: 100%"
            :data="commissions"
            v-loading="commLoading"
          >
            <el-table-column align="center" label="ID" prop="id" width="70"></el-table-column>
            <el-table-column align="center" label="类型" prop="type" width="120"></el-table-column>
            <el-table-column align="center" label="来源订单" prop="orderId" width="100"></el-table-column>
            <el-table-column align="center" label="基数" width="100">
              <template #default="{ row }">¥{{ formatPrice(row.baseAmount) }}</template>
            </el-table-column>
            <el-table-column align="center" label="比例" width="90">
              <template #default="{ row }">{{ formatRate(row.rate) }}</template>
            </el-table-column>
            <el-table-column align="center" label="佣金(gross)" width="110">
              <template #default="{ row }">¥{{ formatPrice(row.amount) }}</template>
            </el-table-column>
            <el-table-column align="center" label="扣减" width="100">
              <template #default="{ row }">¥{{ formatPrice(row.deductAmount) }}</template>
            </el-table-column>
            <el-table-column align="center" label="净额" width="110">
              <template #default="{ row }">¥{{ formatPrice(netOf(row)) }}</template>
            </el-table-column>
            <el-table-column align="center" label="已回滚" width="100">
              <template #default="{ row }">¥{{ formatPrice(row.rollbackAmount) }}</template>
            </el-table-column>
            <el-table-column align="center" label="状态" width="90">
              <template #default="{ row }">
                <el-tag :type="recordStatusType(row.status)" size="small">{{ recordStatusText(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column align="center" label="时间" prop="createTime" width="160"></el-table-column>
          </el-table>
        </div>

        <div v-show="activeTab === 'transactions'">
          <el-table
            :header-cell-style="{ background: '#f7f7f7', color: '#333' }"
            style="width: 100%"
            :data="transactions"
            v-loading="txLoading"
          >
            <el-table-column align="center" label="ID" prop="id" width="70"></el-table-column>
            <el-table-column align="center" label="类型" prop="type" width="120"></el-table-column>
            <el-table-column align="center" label="变动金额" width="120">
              <template #default="{ row }">
                <span :style="{ color: amountColor(row.amount) }">
                  {{ amountSigned(row.amount) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column align="center" label="变动后余额" width="120">
              <template #default="{ row }">¥{{ formatPrice(row.balanceAfter) }}</template>
            </el-table-column>
            <el-table-column align="center" label="来源订单" prop="orderId" width="100"></el-table-column>
            <el-table-column align="center" label="描述" prop="description" min-width="200"></el-table-column>
            <el-table-column align="center" label="时间" prop="createTime" width="160"></el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosconf from '../../api/axiosconf'

export default {
  data() {
    return {
      activeTab: 'commissions',
      account: {},
      accountLoading: false,
      commissions: [],
      commLoading: false,
      transactions: [],
      txLoading: false,
    }
  },
  mounted() {
    this.fetchAccount()
    this.fetchCommissions()
    this.fetchTransactions()
  },
  methods: {
    formatPrice(val) {
      if (val === null || val === undefined) return '0.00'
      return parseFloat(val).toFixed(2)
    },
    formatRate(val) {
      if (val === null || val === undefined) return '--'
      return (parseFloat(val) * 100).toFixed(2) + '%'
    },
    amountColor(val) {
      return Number(val) < 0 ? '#f56c6c' : '#67c23a'
    },
    amountSigned(val) {
      const sign = Number(val) < 0 ? '' : '+'
      return sign + this.formatPrice(val)
    },
    netOf(row) {
      const amount = parseFloat(row.amount || 0)
      const deduct = parseFloat(row.deductAmount || 0)
      return amount - deduct
    },
    recordStatusText(s) {
      return { 0: '已撤销', 1: '已结算', 2: '已回滚' }[s] || '未知'
    },
    recordStatusType(s) {
      return { 0: 'info', 1: 'success', 2: 'warning' }[s] || 'info'
    },
    async fetchAccount() {
      this.accountLoading = true
      try {
        const res = await axiosconf.applicationJson('/mall/admin/platform/account')
        if (res.data && res.data.code === 0) {
          this.account = res.data.data || {}
        } else {
          this.$message.error(res.data.msg || '获取平台账户失败')
        }
      } catch (e) {
        console.error('获取平台账户失败', e)
      } finally {
        this.accountLoading = false
      }
    },
    async fetchCommissions() {
      this.commLoading = true
      try {
        const res = await axiosconf.applicationJson('/mall/admin/platform/commissions')
        if (res.data && res.data.code === 0) {
          this.commissions = res.data.data || []
        }
      } catch (e) {
        console.error('获取平台佣金记录失败', e)
      } finally {
        this.commLoading = false
      }
    },
    async fetchTransactions() {
      this.txLoading = true
      try {
        const res = await axiosconf.applicationJson('/mall/admin/platform/transactions')
        if (res.data && res.data.code === 0) {
          this.transactions = res.data.data || []
        }
      } catch (e) {
        console.error('获取平台账户流水失败', e)
      } finally {
        this.txLoading = false
      }
    },
  }
}
</script>

<style scoped>
.independent-wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}
.content {
  padding: 20px;
}
.title {
  font-size: 14px;
  color: #333;
  margin-bottom: 20px;
}
.title .border {
  display: inline-block;
  width: 3px;
  height: 14px;
  background: #679FF6;
  margin-right: 8px;
  vertical-align: middle;
}
.account-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}
.acc-card {
  flex: 1;
  background: #f7f9fc;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 18px 20px;
}
.acc-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}
.acc-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}
.seg-tabs {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e4e7ed;
  margin-bottom: 16px;
}
.seg-item {
  padding: 10px 2px;
  font-size: 15px;
  color: #606266;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  user-select: none;
}
.seg-item:hover {
  color: #679FF6;
}
.seg-item.is-active {
  color: #679FF6;
  border-bottom-color: #679FF6;
  font-weight: 500;
}
.seg-panes {
  margin-top: 4px;
}
</style>
